import { PortfolioExportJobInformation } from "@/tools/aggregate/portfolio-export/components/PortfolioExportJobInformation";
import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { Alert, List, Skeleton } from "antd";
import { useTranslation } from "react-i18next";
interface PortfolioExportJobTableBodyProps {
  schemas: PortfolioExportJobSchema[];
  totalSchemas: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  loading: boolean;
  decrypting: boolean;
  updateSchema: (schema: PortfolioExportJobSchema) => void;
  onDelete: (id: string) => void;
  onError: (err: string | string[], portfolioId: string) => void;
  onEdit: (props: {
    schema: PortfolioExportJobSchema;
    name: string;
    isPrivate: boolean;
    groupId: string;
  }) => Promise<void>;
}
export const PortfolioExportJobTableBody = ({
  schemas,
  totalSchemas,
  currentPage,
  onPageChange,
  pageSize,
  loading,
  decrypting,
  updateSchema,
  onDelete,
  onError,
  onEdit
}: PortfolioExportJobTableBodyProps) => {
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "comprehensive.portfolioInsights"
  });
  const isProcessing = schemas.filter(({
    status
  }) => ["processing", "pending", "provisioning", "running", "uploading"].includes(status)).length > 0;
  return <>
      {isProcessing && <Alert message={<div className="text-sm font-medium text-sky-700">{t("processingAlert.title")}</div>} description={<div className="text-xs text-sky-700">{t("processingAlert.blurb")}</div>} rootClassName="border-none p-4 mb-4" />}

      <div className="relative overflow-x-scroll">
        <List dataSource={schemas} itemLayout="vertical" pagination={{
        pageSize,
        total: totalSchemas,
        onChange: onPageChange,
        showSizeChanger: false,
        className: "mb-4 mr-5 [&_a]:!no-underline",
        current: currentPage
      }} rowKey="id" className={`mb-4 min-h-36 overflow-hidden rounded-lg border border-gray-200 [&>a]:!no-underline`} size="large" loading={{
        rootClassName: "relative",
        className: "!relative",
        indicator: <div className="!relative !left-0 !top-0 !m-0 !h-full !w-full">
                <CustomSkeleton />
                <CustomSkeleton />
              </div>,
        // Show spinner only during network request
        // Recognises difference between no data and the first decrypting step
        spinning: loading || decrypting && !schemas.length,
        size: "large"
      }} renderItem={schema => {
        // Show skeleton only during decryption
        if (!loading && decrypting) {
          return <CustomSkeleton />;
        }
        return <List.Item
        // TODO: Move JobActions into this property.
        // actions={[<div key="1">Wow</div>]}
        className="!flex !items-center border-0 border-b !border-gray-200 px-4 odd:bg-gray-50 xl:h-[72px]">
                <PortfolioExportJobInformation onDelete={() => onDelete(schema.id)} onEdit={onEdit} schema={schema} updateSchema={updateSchema} onError={onError} />
              </List.Item>;
      }} data-sentry-element="List" data-sentry-source-file="PortfolioExportJobTableBody.tsx" />
      </div>
    </>;
};
const CustomSkeleton = () => <div className="b-0 h-[72px] space-y-2 border-0 border-b !border-gray-200 px-6 py-4 odd:bg-gray-50" data-sentry-component="CustomSkeleton" data-sentry-source-file="PortfolioExportJobTableBody.tsx">
    <div className="flex w-full items-center justify-between">
      <div className="flex flex-col space-y-2">
        <Skeleton.Input active size="small" className="!h-4 !w-[300px]" data-sentry-element="unknown" data-sentry-source-file="PortfolioExportJobTableBody.tsx"></Skeleton.Input>
        <Skeleton.Input active size="small" className="!h-4 !w-[500px]" data-sentry-element="unknown" data-sentry-source-file="PortfolioExportJobTableBody.tsx" />
      </div>
      <div className="hidden space-x-2 lg:flex">
        <Skeleton.Button active size="small" className="!w-[100px]" data-sentry-element="unknown" data-sentry-source-file="PortfolioExportJobTableBody.tsx" />
        <Skeleton.Button active size="small" className="!w-[100px]" data-sentry-element="unknown" data-sentry-source-file="PortfolioExportJobTableBody.tsx" />
        <Skeleton.Button active size="small" className="!w-[30px] !min-w-0" data-sentry-element="unknown" data-sentry-source-file="PortfolioExportJobTableBody.tsx" />
      </div>
    </div>
  </div>;